import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, ArrowLeft, ArrowRight, Tag, Calendar, List } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROD_DOMAIN = "https://www.thesecretseller.co.uk";

// Fix image URLs: replace any hardcoded deployment domain with current origin
function fixImageUrl(url) {
  if (!url) return url;
  // Replace any emergent host URLs with current origin's API
  const currentApi = `${window.location.origin}/api`;
  return url
    .replace(/https?:\/\/[^/]*\.emergent\.host\/api/g, currentApi)
    .replace(/https?:\/\/[^/]*\.emergentagent\.com\/api/g, currentApi)
    .replace(/https?:\/\/[^/]*\.preview\.emergentagent\.com\/api/g, currentApi);
}

function renderInlineFormatting(text) {
  if (!text) return null;
  // Split by bold markers and links
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Check for links [text](url)
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);

    let nextMatch = null;
    let matchType = null;

    if (linkMatch && (!boldMatch || linkMatch.index < boldMatch.index)) {
      nextMatch = linkMatch;
      matchType = "link";
    } else if (boldMatch) {
      nextMatch = boldMatch;
      matchType = "bold";
    }

    if (!nextMatch) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    if (nextMatch.index > 0) {
      parts.push(<span key={key++}>{remaining.substring(0, nextMatch.index)}</span>);
    }

    if (matchType === "link") {
      const isInternal = nextMatch[2].startsWith("/");
      if (isInternal) {
        parts.push(<Link key={key++} to={nextMatch[2]} className="text-[#0D2D54] font-medium underline hover:text-[#1A3D6B]">{nextMatch[1]}</Link>);
      } else {
        parts.push(<a key={key++} href={nextMatch[2]} target="_blank" rel="noopener noreferrer" className="text-[#0D2D54] font-medium underline hover:text-[#1A3D6B]">{nextMatch[1]}</a>);
      }
    } else {
      parts.push(<strong key={key++} className="text-[#0D2D54] font-semibold">{nextMatch[1]}</strong>);
    }

    remaining = remaining.substring(nextMatch.index + nextMatch[0].length);
  }

  return parts;
}

function renderBlocks(blocks) {
  return blocks.map((block, i) => {
    switch (block.type) {
      case "h2":
        return <h2 key={i} id={`section-${i}`} className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mt-10 mb-4">{block.text}</h2>;
      case "h3":
        return <h3 key={i} className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mt-8 mb-3">{block.text}</h3>;
      case "paragraph":
        return <p key={i} className="text-sm text-[#6B7280] leading-relaxed my-4">{renderInlineFormatting(block.text)}</p>;
      case "bullet_list":
        return (
          <ul key={i} className="list-disc list-inside space-y-2 text-[#6B7280] text-sm leading-relaxed my-4 ml-2">
            {block.items?.map((item, j) => <li key={j}>{renderInlineFormatting(item)}</li>)}
          </ul>
        );
      case "numbered_list":
        return (
          <ol key={i} className="list-decimal list-inside space-y-2 text-[#6B7280] text-sm leading-relaxed my-4 ml-2">
            {block.items?.map((item, j) => <li key={j}>{renderInlineFormatting(item)}</li>)}
          </ol>
        );
      case "image":
        return block.url ? (
          <figure key={i} className="my-8">
            <img
              src={fixImageUrl(block.url)}
              alt={block.alt || ""}
              className="w-full h-auto max-w-full"
              loading="lazy"
              decoding="async"
            />
            {block.alt && <figcaption className="text-xs text-[#6B7280] mt-2 text-center">{block.alt}</figcaption>}
          </figure>
        ) : null;
      case "faq":
        return (
          <details key={i} className="my-4 border border-[#E8E8E4] group">
            <summary className="flex items-center justify-between cursor-pointer p-4 text-sm font-medium text-[#0D2D54] list-none hover:bg-[#FAFAF8]">
              <span>{block.question}</span>
              <span className="text-[#6B7280] group-open:rotate-90 transition-transform">+</span>
            </summary>
            <div className="px-4 pb-4 text-sm text-[#6B7280] leading-relaxed">
              {renderInlineFormatting(block.answer)}
            </div>
          </details>
        );
      default:
        return null;
    }
  });
}

// Fallback: render legacy markdown content
function renderLegacyContent(content) {
  return content.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) return <h2 key={i} className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mt-8 mb-4">{block.replace("## ", "")}</h2>;
    if (block.startsWith("### ")) return <h3 key={i} className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mt-6 mb-3">{block.replace("### ", "")}</h3>;
    if (block.startsWith("- ")) {
      const items = block.split("\n").filter((l) => l.startsWith("- "));
      return <ul key={i} className="list-disc list-inside space-y-2 text-[#6B7280] text-sm my-4">{items.map((item, j) => <li key={j}>{item.replace("- ", "")}</li>)}</ul>;
    }
    const parts = block.split(/\*\*(.*?)\*\*/g);
    return <p key={i} className="text-sm text-[#6B7280] leading-relaxed my-4">{parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-[#0D2D54] font-medium">{part}</strong> : part)}</p>;
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`).then((res) => {
      setPost(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  if (loading) {
    return <main className="pt-20 min-h-screen"><div className="max-w-3xl mx-auto px-6 py-20 text-center"><p className="text-[#6B7280]">Loading...</p></div></main>;
  }

  if (!post) {
    return <main className="pt-20 min-h-screen"><div className="max-w-3xl mx-auto px-6 py-20 text-center"><h1 className="font-['Outfit'] text-2xl text-[#0D2D54] mb-4">Post not found</h1><Link to="/blog" className="text-sm text-[#0D2D54] hover:underline">Back to blog</Link></div></main>;
  }

  const formatDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const hasBlocks = post.content_blocks && post.content_blocks.length > 0;
  const blocks = hasBlocks ? post.content_blocks : [];
  const h2Blocks = blocks.filter((b) => b.type === "h2");
  const faqBlocks = blocks.filter((b) => b.type === "faq");

  // Schema
  const canonicalUrl = post.canonical_url || `${PROD_DOMAIN}/blog/${post.slug}`;
  const metaTitle = post.meta_title || `${post.title} | The Secret Seller Blog`;
  const metaDesc = post.meta_description || post.excerpt;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": metaDesc,
    "author": { "@type": "Organization", "name": post.author || "The Secret Seller" },
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "publisher": { "@type": "Organization", "name": "The Secret Seller" },
    ...(post.featured_image && { "image": post.featured_image }),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": PROD_DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${PROD_DOMAIN}/blog` },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": canonicalUrl },
    ],
  };

  const faqSchema = faqBlocks.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqBlocks.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  } : null;

  return (
    <main data-testid="blog-post-page" className="pt-20">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.og_title || metaTitle} />
        <meta property="og:description" content={post.og_description || metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        {post.noindex && <meta name="robots" content="noindex" />}
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify((articleSchema)) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify((breadcrumbSchema)) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify((faqSchema)) }} />}

      <article className="py-12 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-[#6B7280] mb-6">
            <Link to="/" className="hover:text-[#0D2D54]">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#0D2D54]">Blog</Link>
            <span>/</span>
            <span className="text-[#0D2D54]">{post.title}</span>
          </nav>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-[#758C7B] font-medium uppercase tracking-[0.1em]">{post.category}</span>
              <span className="flex items-center gap-1 text-xs text-[#6B7280]"><Clock size={12} /> {post.reading_time} min read</span>
              <span className="flex items-center gap-1 text-xs text-[#6B7280]"><Calendar size={12} /> {formatDate(post.created_at)}</span>
            </div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-light text-[#0D2D54] tracking-tight leading-tight">
              {post.title}
            </h1>
          </div>

          {/* Featured image */}
          {post.featured_image && (
            <div className="mb-10 overflow-hidden">
              <img
                src={fixImageUrl(post.featured_image)}
                alt={post.featured_image_alt || post.title}
                className="w-full h-auto max-w-full"
                loading="eager"
                decoding="async"
              />
            </div>
          )}

          {/* Table of Contents */}
          {h2Blocks.length > 0 && (
            <div data-testid="table-of-contents" className="bg-[#FAFAF8] border border-[#E8E8E4] p-5 mb-10">
              <div className="flex items-center gap-2 mb-3">
                <List size={14} className="text-[#0D2D54]" />
                <h3 className="text-sm font-medium text-[#0D2D54]">Table of Contents</h3>
              </div>
              <ol className="space-y-1.5">
                {h2Blocks.map((h2, i) => {
                  const blockIndex = blocks.indexOf(h2);
                  return (
                    <li key={i}>
                      <a href={`#section-${blockIndex}`} className="text-sm text-[#6B7280] hover:text-[#0D2D54] hover:underline transition-colors">
                        {h2.text}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Content */}
          <div data-testid="blog-content">
            {hasBlocks ? renderBlocks(blocks) : renderLegacyContent(post.content)}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-[#E8E8E4]">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag size={14} className="text-[#6B7280]" />
                {post.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-[#FAFAF8] text-[#6B7280] px-3 py-1 border border-[#E8E8E4]">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-[#FAFAF8] border border-[#E8E8E4] p-8 text-center">
            <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-3">Ready to try virtual staging?</h3>
            <p className="text-sm text-[#6B7280] mb-5">From £55 per image. Delivered in 48 hours. No subscription.</p>
            <Link to="/order" data-testid="blog-cta" className="inline-flex items-center gap-2 bg-[#0D2D54] text-white px-8 py-3 text-sm font-semibold hover:bg-[#1A3D6B] transition-all">
              Order Virtual Staging <ArrowRight size={14} />
            </Link>
          </div>

          {/* Related posts */}
          {post.related_posts && post.related_posts.length > 0 && (
            <div className="mt-16">
              <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {post.related_posts.map((related, i) => (
                  <Link key={related.id} to={`/blog/${related.slug}`} data-testid={`related-post-${i}`} className="group block border border-[#E8E8E4] p-4 hover:border-[#758C7B] transition-colors">
                    <h4 className="text-sm font-medium text-[#0D2D54] group-hover:text-[#1A3D6B] mb-2 line-clamp-2">{related.title}</h4>
                    <span className="text-xs text-[#6B7280]">{related.reading_time} min read</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </main>
  );
}
